/** Display helpers. Timestamps are stored as UTC and shown in local time. */

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(date);
}

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

/** Local date/time with the timezone named, for anything money-related. */
export function formatExact(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'full', timeStyle: 'long',
  }).format(date);
}

export function relativeTime(value?: string | null): string {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((then - Date.now()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000], ['month', 2592000], ['day', 86400],
    ['hour', 3600], ['minute', 60], ['second', 1],
  ];
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size || unit === 'second') {
      return rtf.format(Math.round(seconds / size), unit);
    }
  }
  return '';
}

export function timeZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'local time';
  }
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Out for signature',
  in_progress: 'In progress',
  finalizing: 'Preparing final document',
  finalization_failed: 'Finalization failed',
  completed: 'Completed',
  declined: 'Declined',
  voided: 'Withdrawn',
  expired: 'Expired',
  pending: 'Not yet notified',
  delivery_failed: 'Delivery failed',
  authenticated: 'Identity verified',
  viewed: 'Opened',
  waiting: 'Waiting for earlier signer',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] || status.replace(/_/g, ' ');
}

export function statusTone(status: string): string {
  switch (status) {
    case 'completed': return 'ok';
    case 'declined': case 'voided': case 'expired': case 'finalization_failed':
    case 'delivery_failed': return 'danger';
    case 'sent': case 'in_progress': case 'viewed': case 'authenticated': return 'info';
    case 'finalizing': return 'warn';
    default: return '';
  }
}

export function shortDigest(digest?: string | null, length = 12): string {
  if (!digest) return '—';
  return digest.slice(0, length) + '…';
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : plural || singular + 's'}`;
}

export function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((part) => part[0]).join('').slice(0, 3).toUpperCase();
}
